import React, { useEffect } from 'react';

import { Stack, Text } from '@mantine/core';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import { CellProps } from 'react-table';
import { AthleteEvent } from 'types';

interface FilterTerms {
  timeRange: 'thirtyDays' | 'ninetyDays' | 'sixMonths' | 'year';
  type: 'attended' | 'all';
}

const today = new Date();
today.setHours(0);
today.setMinutes(0);
today.setSeconds(0);

const AttendanceTableContainer = ({
  searchFilters,
  setTableData,
  tableData,
  getStartDateRange,
}: {
  searchFilters: FilterTerms;
  setTableData: React.Dispatch<React.SetStateAction<AthleteEvent[]>>;
  tableData: AthleteEvent[];
  getStartDateRange: () => string;
}): JSX.Element => {
  const { session } = useAuth();
  const { dateFormat } = useLocalization();

  const { data, isSuccess, isError, isLoading, hasNextPage, fetchNextPage } = useAthleteEvents({
    athlete: session?.athlete?.athleteId,
    hideWaitlist: true,
    startDateRange: getStartDateRange(),
    endDateRange: dayjs(today).format('YYYY-MM-DD'),
    type: 'past',
    ordering: '-start_date',
    limit: '100',
    isCheckedIn: searchFilters.type === 'attended' ? true : undefined,
  });

  useEffect(() => {
    if (data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [data, setTableData]);

  const columns = [
    {
      Header: 'Date',
      accessor: 'startDate',
      Cell: ({ row }: CellProps<AthleteEvent>) => (
        <>
          <Text>{dayjs(row.original.startDate).format(dateFormat)}</Text>
          <Text>
            {dayjs(new Date(`${row.original.startDate}T${row.original.startTime}`)).format('h:mma')}
          </Text>
        </>
      ),
    },
    {
      Header: 'Event',
      accessor: 'name',
    },
    {
      Header: 'Coach',
      accessor: 'coachName',
    },
    {
      Header: 'Attended',
      accessor: 'isCheckedIn',
      Cell: ({ row }: CellProps<AthleteEvent>) => <Text>{row.original.isCheckedIn ? 'Yes' : 'No'}</Text>,
    },
  ];

  return (
    <>
      <Stack className='content' w='100%' p='0'>
        <Table
          tableData={tableData}
          columns={columns}
          isSuccess={isSuccess}
          isError={isError}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      </Stack>
    </>
  );
};

export default AttendanceTableContainer;
